/**
 * CloneEntityCommand - Generic command for cloning entities (StepNode, StrategyBlock)
 *
 * Implements the Command pattern for undoable entity cloning.
 * Delegates to FlowCoordinator for actual cloning logic.
 *
 * Generic Architecture:
 * - TEntity extends FlowEntity (StepNode, StrategyBlock)
 * - TService (StepNodeScreenService, StrategyBlockService)
 * - Uses ONLY generic coordinator methods (cloneEntity, deleteEntities)
 *
 * Cloning Behavior:
 * - Creates deep copy of source entity with new UUID
 * - ALL edges are CLEARED (no transitions are cloned)
 * - For blocks: Also clones all child StepNode screens (without transitions)
 * - Position is offset from original (calculated from constants)
 * - Name gets " (Copy)" suffix
 *
 * Usage:
 * ```typescript
 * // For Block
 * const command = new CloneEntityCommand<StrategyBlock, StrategyBlockService>(
 *   'source-block-id',
 *   { x: 562, y: 0 },  // BLOCK_WIDTH + BLOCK_WIDTH/3
 *   blockContext
 * );
 *
 * await commandManager.execute(command);
 * await commandManager.undo();  // Deletes cloned block
 * ```
 *
 * Related Requirements:
 * - FR-CLONE-001: Clone entity with new UUID
 * - FR-CLONE-002: Clear all edges before creating clone
 * - FR-CLONE-003: Clone screens for blocks without transitions
 * - FR-P1-003: Commands use coordinator ONLY for all operations
 */

import { Command, CommandResult } from './Command';
import { CommandContext } from './CommandContext';
import type { FlowEntity, PositionOnConfigurator } from '@modules/reactFlow/types/flow-canvas-types';

/**
 * CloneEntityCommand - Generic command for cloning flow entities
 *
 * @template TEntity - Entity type extending FlowEntity (StepNode, StrategyBlock)
 * @template TService - Firebase service type (StepNodeScreenService, StrategyBlockService)
 */
export class CloneEntityCommand<
  TEntity extends FlowEntity,
  TService
> implements Command {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  private clonedEntityId?: string;

  /**
   * @param sourceEntityId - ID of entity to clone
   * @param offset - Position offset from source entity
   * @param context - Command execution context
   */
  constructor(
    private sourceEntityId: string,
    private offset: PositionOnConfigurator,
    private context: CommandContext<TEntity, TService>
  ) {
    this.id = `clone-entity-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.description = `Clone entity ${sourceEntityId.slice(0, 8)}`;
    this.timestamp = Date.now();
  }

  /**
   * Execute command - Clone entity via coordinator
   *
   * Flow:
   * 1. Validate source entity exists
   * 2. Call coordinator.cloneEntity() - handles:
   *    - Deep copy with new UUID
   *    - Edge clearing (all transitions removed)
   *    - Screen cloning for blocks (without transitions)
   *    - Registry + Canvas + Firebase operations
   * 3. Store cloned entity ID for undo
   * 4. Return success result
   *
   * @returns Promise resolving with command result
   */
  async execute(): Promise<CommandResult> {
    try {
      console.log(`[CloneEntityCommand] Executing clone for entity: ${this.sourceEntityId}`);

      // Validate source exists
      const sourceEntity = this.context.coordinator.getEntity(this.sourceEntityId);
      if (!sourceEntity) {
        throw new Error(`Source entity ${this.sourceEntityId} not found`);
      }

      // Coordinator handles EVERYTHING:
      // - Deep copy entity data
      // - Clear all edges (via EdgeCleaner)
      // - Clone StepNode screens for blocks (via the coordinator-owned V2 service)
      // - Update Registry + Canvas + Firebase
      const result = await this.context.coordinator.cloneEntity(
        this.sourceEntityId,
        {
          x: this.offset.x ?? 0,
          y: this.offset.y ?? 0
        }
      );

      // Use entityId (not Firestore id) for coordinator operations
      this.clonedEntityId = result.entity.entityId;

      console.log(`[CloneEntityCommand] ✅ Entity cloned: ${this.clonedEntityId}`);

      return {
        success: true,
        affectedEntities: { blocks: [this.clonedEntityId!] }
      };
    } catch (error) {
      console.error('[CloneEntityCommand] ❌ Clone failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Undo command - Delete cloned entity via coordinator
   *
   * Flow:
   * 1. Call coordinator.deleteEntities() - handles:
   *    - Delete cloned screens for blocks
   *    - Registry + Canvas + Firebase cleanup
   * 2. Return success result
   *
   * @returns Promise resolving with command result
   */
  async undo(): Promise<CommandResult> {
    if (!this.clonedEntityId) {
      return {
        success: false,
        error: new Error('No cloned entity to undo'),
        affectedEntities: {}
      };
    }

    try {
      console.log(`[CloneEntityCommand] Undoing clone for entity: ${this.clonedEntityId}`);

      // Coordinator handles EVERYTHING (Registry + Canvas + Firebase + cascading screen deletes)
      await this.context.coordinator.deleteEntities([this.clonedEntityId]);

      console.log('[CloneEntityCommand] ✅ Undo successful');

      return {
        success: true,
        affectedEntities: { blocks: [this.clonedEntityId] }
      };
    } catch (error) {
      console.error('[CloneEntityCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Undo failed'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Check if command can execute
   *
   * @returns True if source entity exists
   */
  canExecute(): boolean {
    const sourceEntity = this.context.coordinator.getEntity(this.sourceEntityId);
    return !!sourceEntity;
  }

  /**
   * Check if command can undo
   *
   * @returns True if cloned entity was created
   */
  canUndo(): boolean {
    return !!this.clonedEntityId;
  }
}
